import { Checkbox, cn, Input, Text } from '@risksmart-app/atomic-ui';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMemo, useRef, useState } from 'react';

export interface CheckboxListOption {
  value: string;
  label: string;
}

interface SearchableCheckboxListProps {
  options: CheckboxListOption[];
  /** Selected value ids — the persisted rule `values` shape (`string[]`). */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  searchPlaceholder?: string;
  testId?: string;
}

const ROW_HEIGHT = 32;
const MAX_LIST_HEIGHT = 240;
const EMPTY_STATE_HEIGHT = 32;

/**
 * The inline checkbox "toggle area" used by the conditional-logic rule builder,
 * with a search box and a virtualised row list so it stays performant for large
 * option sets (e.g. all org users) without rendering N checkboxes at once.
 */
export const SearchableCheckboxList = ({
  options,
  value,
  onChange,
  disabled = false,
  searchPlaceholder = 'Search',
  testId,
}: SearchableCheckboxListProps) => {
  const [query, setQuery] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const filteredOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(trimmed)
    );
  }, [options, query]);

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  const toggle = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue]
    );
  };

  const hasMatches = filteredOptions.length > 0;
  // Drive an explicit, transitionable height so the list grows/shrinks smoothly
  // as the search narrows the result set (a content-driven `auto` height can't
  // be animated). Capped at MAX_LIST_HEIGHT (scrolls beyond that).
  const listHeight = hasMatches
    ? Math.min(MAX_LIST_HEIGHT, virtualizer.getTotalSize())
    : EMPTY_STATE_HEIGHT;

  return (
    <div className={cn('flex flex-col gap-2')}>
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={searchPlaceholder}
        disabled={disabled}
        data-testid={testId}
      />
      <div
        ref={scrollRef}
        role={'group'}
        className={cn(
          'overflow-y-auto transition-[height] duration-200 ease-out motion-reduce:transition-none'
        )}
        style={{ height: listHeight }}
      >
        {hasMatches ? (
          <div
            style={{
              height: virtualizer.getTotalSize(),
              position: 'relative',
              width: '100%',
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const option = filteredOptions[virtualRow.index];

              return (
                <label
                  key={option.value}
                  className={cn(
                    'absolute left-0 top-0 flex w-full items-center gap-2 rounded pl-1.5 text-base cursor-pointer'
                  )}
                  style={{
                    height: virtualRow.size,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <Checkbox
                    size={'md'}
                    aria-label={option.label}
                    checked={value.includes(option.value)}
                    onCheckedChange={() => toggle(option.value)}
                    disabled={disabled}
                  />
                  <span className={cn('min-w-0 flex-1 truncate')}>
                    {option.label}
                  </span>
                </label>
              );
            })}
          </div>
        ) : (
          <Text
            preset={'body-sm'}
            className={cn('px-1.5 py-1 text-muted-foreground')}
          >
            {'No matches found'}
          </Text>
        )}
      </div>
    </div>
  );
};
